import { useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { MaskedInput, createDefaultMaskGenerator } from 'react-hook-mask';
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../hooks/useLanguage";

import Select from "react-select";
import Alert from "../Alert";
import api from "../../services/api";

import styles from './index.module.scss';

const maskGenerator = createDefaultMaskGenerator('(99) 99999-9999');

export default function Form({ handleOpenModal }) {
  const { register, control, handleSubmit, reset, formState: { errors, isDirty, isValid } } = useForm({
    mode: "onBlur"
  });
  const [optionTypeCompany, setOptionTypeCompany] = useState([]);
  const [optionCountry, setOptionCountry] = useState([]);

  const { t } = useTranslation();
  const { language, countryNumber } = useLanguage();

  const onSubmit = async (props) => {
    const { celular, telefone, tipoEmpresaID } = props;
    const phone = `+${telefone?.countryCode || countryNumber}${celular}`;

    const data = {
      ...props,
      celular: phone,
      telefone: phone,
      tipoEmpresaID: tipoEmpresaID?.id
    }

    try {
      await api.post('QRCNContato', data, {
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        }
      });

      Alert.fire({
        icon: 'success',
        title: 'Formulário enviado!'
      }).then(() => {
        reset();
      });
    } catch (e) {
      console.error(e);

      Alert.fire({
        icon: 'error',
        title: 'Ouve um erro no envio'
      });
    }
  }

  const handleTypeCompany = async () => {
    const { data } = await api.get('QRCNTipoEmpresa/lista');
    setOptionTypeCompany(data.value);
  }

  const handleCountry = async () => {
    const { data } = await api.get('pais/lista');
    setOptionCountry(data.value);
  }

  const optionCountryFindDefaultValue = useMemo(() => {
    const filter = optionCountry.filter(({ countryCode }) => countryCode === countryNumber);

    return filter[filter.length - 1];
  }, [optionCountry, countryNumber]);

  useEffect(() => {
    handleTypeCompany();
    handleCountry();
  }, []);

  return (
    <aside className={styles.form}>
      <h1>{t('request_demo_form')}</h1>
      <p>{t('fill_the_form')}</p>

      <form onSubmit={handleSubmit(onSubmit)}>
        <input value="1" type="hidden" {...register("origemContatoID")} />
        <input value={language} type="hidden" {...register("idiomaID")} />

        <div className={styles.row}>
          <div>
            <label htmlFor="name">{t('name_form')}</label>
            <input
              id="name"
              type="text"
              placeholder={t('name_form_placeholder')}
              {...register("nome", {
                required: true,
                maxLength: "150"
              })}
            />
            {errors.nome?.type === 'required' && <p className={styles.alert} role="alert">{t('required_field')}</p>}
          </div>

          <div>
            <label htmlFor="last-name">{t('surname_form')}</label>
            <input
              id="last-name"
              type="text"
              placeholder={t('surname_form_placeholder')}
              {...register("sobrenome", {
                required: true,
                maxLength: "150"
              })}
            />
            {errors.sobrenome?.type === 'required' && <p className={styles.alert} role="alert">{t('required_field')}</p>}
          </div>
        </div>

        <div className={styles.row}>
          <div>
            <label htmlFor="company">{t('company_form')}</label>
            <input
              placeholder={t('company_form_placeholder')}
              id="company"
              type="text"
              {...register("empresa", {
                required: true,
                maxLength: "150"
              })}
            />
            {errors.empresa?.type === 'required' && <p className={styles.alert} role="alert">{t('required_field')}</p>}
          </div>

          <div>
            <label htmlFor="email">{t('email_form')}</label>
            <input
              id="email"
              type="text"
              placeholder={t('email_form_placeholder')}
              {...register("email", {
                required: true,
                maxLength: "150",
                pattern: /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/
              })}
            />
            {errors.email?.type === 'required' && <p className={styles.alert} role="alert">{t('required_field')}</p>}
            {errors.email?.type === 'pattern' && <p className={styles.alert} role="alert">{t('email_invalid_form')}</p>}
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.company}>
            <label htmlFor="company-type">{t('type_of_company_form')}</label>
            <Controller
              control={control}
              id="company-type"
              name="tipoEmpresaID"
              rules={{
                required: true
              }}
              render={({ field: { onChange, value, ref } }) => (
                <Select
                  placeholder={t('type_of_company_form_placeholder')}
                  options={optionTypeCompany}
                  inputRef={ref}
                  value={value}
                  onChange={onChange}
                  isSearchable={true}
                  getOptionValue={(option) => option.description}
                  formatOptionLabel={({ description }) => (
                    <span>{description}</span>
                  )}
                />
              )}
            />
            {errors.tipoEmpresaID?.type === 'required' && <p className={styles.alert} role="alert">{t('required_field')}</p>}
          </div>

          <div>
            <label htmlFor="service">{t('responsibility_form')}</label>
            <input
              id="service"
              type="text"
              placeholder={t('responsibility_form_placeholder')}
              {...register("cargo", {
                required: true,
                maxLength: "60"
              })}
            />
            {errors.cargo?.type === 'required' && <p className={styles.alert} role="alert">{t('required_field')}</p>}
          </div>
        </div>

        <div className={styles.country}>
          <label htmlFor="phone">{t('phone_form')}</label>
          <Controller
            control={control}
            name="celular"
            id="phone"
            rules={{
              required: true,
              maxLength: 11
            }}
            render={({ field: { onChange, value } }) => (
              <MaskedInput
                onChange={onChange}
                value={value}
                placeholder="(00) 00000-0000"
                maskGenerator={maskGenerator}
                className={styles.countryInput}
              />
            )}
          />
          {optionCountryFindDefaultValue && (
            <>
              <Controller
                control={control}
                name="telefone"
                render={({ field: { onChange, ref } }) => (
                  <Select
                    placeholder="ex: USA"
                    options={optionCountry}
                    inputRef={ref}
                    defaultValue={optionCountryFindDefaultValue}
                    onChange={(value) => onChange(value)}
                    isSearchable={true}
                    getOptionValue={(option) => option.abbreviation}
                    formatOptionLabel={({ abbreviation, image }) => (
                      <div className={styles.countryBox}>
                        <img src={image} alt={abbreviation} />
                        <span>{abbreviation}</span>
                      </div>
                    )}
                  />
                )}
              />
              {errors.celular || errors.telefone && <p className={styles.alert} role="alert">{t('required_field')}</p>}
            </>
          )}
        </div>

        <div className={styles.row}>
          <label htmlFor="message">{t('message_form')}</label>
          <textarea
            id="message"
            cols="30"
            rows="10"
            placeholder={t('message_form_placeholder')}
            {...register("mensagem", {
              required: true,
              maxLength: "1024"
            })}
          ></textarea>
          {errors.mensagem?.type === 'required' && <p className={styles.alert} role="alert">{t('required_field')}</p>}
        </div>

        <div className={styles.row}>
          <label className={styles.checkbox} htmlFor="check">
            <button type="button" data-link="link" onClick={handleOpenModal}>
              <p>{t('terms_and_conditions_part1_form')} <h6>{t('terms_and_conditions')}</h6> {t('terms_and_conditions_part2_form')}</p>
            </button>
            <input
              id="check"
              type="checkbox"
              {...register("check", {
                required: true
              })}
            />
            <span className="checkmark"></span>
            {errors.check?.type === 'required' && <p className={styles.alert} role="alert">{t('required_field')}</p>}
          </label>
        </div>

        <button disabled={!isDirty || !isValid} type="submit">{t('submit_request_demo_form')}</button>
      </form>
    </aside>
  )
}